import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Mail, Lock, ArrowRight, Loader2, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { analytics } from '../utils/analytics';

const features = [
  'Upload lecture slides & homework PDFs',
  'Get instant AI-generated practice exams',
  'Study with personalized flashcards & quizzes',
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      await signin(email, password);
      analytics.loginSuccess(email);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      let errorMsg = error?.message || 'Invalid email or password';

      if (errorMsg.includes('body stream')) {
        errorMsg = 'Invalid email or password';
      } else if (errorMsg.includes('Invalid login') || errorMsg.includes('invalid_credentials')) {
        errorMsg = 'Invalid email or password';
      } else if (errorMsg.includes('Email not confirmed')) {
        errorMsg = 'Please confirm your email before logging in';
      }

      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex w-2/5 bg-primary flex-col justify-between p-10 text-white">
        <Link to="/" className="flex items-center gap-2 w-fit">
          <div className="w-10 h-10 bg-white/20 flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-black text-xl tracking-tight">CALC AI</span>
        </Link>

        <div>
          <h2 className="font-heading font-black text-4xl leading-tight mb-8">
            Master Calculus With AI-Powered Study Tools
          </h2>
          <div className="space-y-4">
            {features.map((text) => (
              <div key={text} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-white/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span className="text-white/90 text-sm leading-relaxed">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/40 text-xs">© Powered by FHYAS LLC.</p>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col bg-white">
        {/* Mobile-only header */}
        <header className="lg:hidden border-b-2 border-border px-6 py-4">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <div className="w-10 h-10 bg-primary flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <span className="font-heading font-black text-lg tracking-tight">CALC AI</span>
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <motion.div
            className="w-full max-w-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="mb-8">
              <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                Welcome back
              </span>
              <h1 className="font-heading font-black text-4xl tracking-tight mt-1">
                Log In
              </h1>
              <p className="text-muted-foreground mt-2 text-sm">
                Enter your credentials to access your account
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-mono text-xs uppercase tracking-wider">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@university.edu"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 border-2 rounded-none"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="font-mono text-xs uppercase tracking-wider">
                    Password
                  </Label>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-12 border-2 rounded-none"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 font-bold uppercase tracking-wide mt-2"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing In...
                  </>
                ) : (
                  <>
                    Log In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-8 pt-6 border-t-2 border-border text-center">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link
                  to="/signup"
                  className="text-primary font-semibold hover:underline"
                  data-testid="goto-signup-link"
                >
                  Sign up for free
                </Link>
              </p>
            </div>
          </motion.div>
        </main>

        {/* Mobile-only footer */}
        <footer className="lg:hidden border-t-2 border-border py-4 px-6 text-center">
          <p className="text-xs text-muted-foreground">© Powered by FHYAS LLC.</p>
        </footer>
      </div>
    </div>
  );
}
